import { ProjectFile } from '@portal-core/project-files/models/project-file.model';
import { Observable } from 'rxjs';

/** Parameters for a tree data request. */
export interface TreeDataRequest {
  projectId: number;
  branchName: string;
  treePath: string;
  includeFiles: boolean;
  fileFilter: string;
  committedFilter: boolean;
}

/** A single file that has been added/updated and the flags the tree should apply to it. */
export interface ProjectFileTreeUpdateAddition {
  /** Full file path. */
  filePath: string;
  /** True when this file has uncommitted workspace changes (drives the uncommitted dot indicator). */
  isUncommitted: boolean;
  /** True when this file is a collab file. */
  isCollabFile: boolean;
}

/** Mutations to apply to an existing tree node. Omitted flags are left unchanged. */
export interface ProjectFileTreeUpdateChange {
  /** Full file path of the node to update. */
  filePath: string;
  /** When set, replaces the node's uncommitted flag (clears propagate to ancestors when no sibling remains uncommitted). */
  isUncommitted?: boolean;
  /** When set, replaces the leaf's collab flag. Ignored for folders. */
  isCollabFile?: boolean;
}

/** Describes file additions/removals/updates that the tree should react to. */
export interface ProjectFileTreeUpdate {
  added: ProjectFileTreeUpdateAddition[];
  removed: string[];
  updated: ProjectFileTreeUpdateChange[];
}

/**
 * Abstract data provider for the project files tree.
 * Implementations supply tree data from different sources:
 * - ProjectFilesDataProvider: git-only (committed files from the .NET API)
 * - WorkspaceFilesDataProvider: git + collab workspace files merged, with real-time WebSocket updates
 */
export abstract class ProjectFilesTreeDataProvider {
  /** Fetch tree nodes for a given path. The provider handles all merging/filtering internally. */
  abstract getTree$(request: TreeDataRequest): Observable<ProjectFile[]>;

  /** Emits when files are added/removed and the tree should update nodes. Emits null if the provider never pushes updates. */
  abstract readonly fileUpdates$: Observable<ProjectFileTreeUpdate | null>;

  /**
   * Returns true if any file under `folderPath` has uncommitted workspace changes.
   * Providers without workspace awareness should return false.
   */
  abstract hasUncommittedDescendant(folderPath: string): boolean;

  /**
   * Cleanup hook called by whoever owns this instance: the component that provides it, or the tree
   * when the tree constructed it. Consumers that merely inject the provider must not call this.
   */
  abstract dispose(): void;
}
